import React, { useState, useEffect } from 'react';
import { Save, Download, Upload, Trash2, Calendar, BarChart3, Users, Target } from 'lucide-react';
import { Experiment, SimulationResults, SimulationRequest } from '../types/api';
import { cn, formatPercentage, formatNumber } from '../lib/utils';

interface ExperimentManagerProps {
  currentResults: SimulationResults | null;
  currentParameters: SimulationRequest | null;
  onLoadExperiment: (experiment: Experiment) => void;
}

const ExperimentManager: React.FC<ExperimentManagerProps> = ({
  currentResults,
  currentParameters,
  onLoadExperiment
}) => {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [experimentName, setExperimentName] = useState('');
  const [selectedExperiments, setSelectedExperiments] = useState<string[]>([]);

  useEffect(() => {
    loadExperiments();
  }, []);

  const loadExperiments = () => {
    try {
      const saved = localStorage.getItem('ai_survey_experiments');
      if (saved) {
        const parsed = JSON.parse(saved);
        setExperiments(parsed);
      }
    } catch (error) {
      console.error('Error loading experiments:', error);
    }
  };

  const saveExperiments = (newExperiments: Experiment[]) => {
    try {
      localStorage.setItem('ai_survey_experiments', JSON.stringify(newExperiments));
      setExperiments(newExperiments);
    } catch (error) {
      console.error('Error saving experiments:', error);
      alert('Error saving experiment. Storage space full?');
    }
  };

  const handleSaveExperiment = () => {
    if (!currentResults || !currentParameters || !experimentName.trim()) {
      alert('Experiment name required and results must be available');
      return;
    }

    const newExperiment: Experiment = {
      id: Date.now().toString(),
      name: experimentName.trim(),
      timestamp: new Date().toISOString(),
      parameters: currentParameters,
      results: currentResults
    };

    const updatedExperiments = [newExperiment, ...experiments];
    saveExperiments(updatedExperiments);
    setExperimentName('');
    setShowSaveDialog(false);
  };

  const handleDeleteExperiment = (id: string) => {
    if (window.confirm('Delete this experiment?')) {
      const updated = experiments.filter(exp => exp.id !== id);
      saveExperiments(updated);
    }
  };

  const handleExportExperiments = () => {
    const dataStr = JSON.stringify(experiments, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `experiments_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportExperiments = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string);
        if (Array.isArray(imported)) {
          const existingIds = new Set(experiments.map(exp => exp.id));
          const newExperiments = imported.filter((exp: Experiment) => !existingIds.has(exp.id));
          const updated = [...experiments, ...newExperiments];
          saveExperiments(updated);
          alert(`Imported ${newExperiments.length} new experiments`);
        }
      } catch {
        alert('Error importing file. Invalid format.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExperimentSelection = (id: string) => {
    setSelectedExperiments(prev => 
      prev.includes(id) 
        ? prev.filter(expId => expId !== id)
        : [...prev, id]
    );
  };

  const getComparisonData = () => {
    const selected = experiments.filter(exp => selectedExperiments.includes(exp.id));
    return selected.map(exp => ({
      name: exp.name,
      accuracy: exp.results.accuracy || 0,
      si_percentage: ((exp.results.si_count / (exp.results.si_count + exp.results.no_count)) * 100) || 0,
      sample_size: exp.parameters.sample_size,
      variables_count: exp.parameters.selected_variables.length
    }));
  };

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-2">
          <Save className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Experiments</h2>
        </div>
        
        <div className="flex items-center space-x-1.5">
          <button
            onClick={() => setShowSaveDialog(true)}
            disabled={!currentResults}
            className="px-2.5 py-1.5 bg-foreground text-background rounded text-xs flex items-center space-x-1 hover:bg-foreground/90 disabled:bg-muted disabled:text-muted-foreground transition-colors"
          >
            <Save className="h-3 w-3" />
            <span>Save</span>
          </button>
          
          <button
            onClick={handleExportExperiments}
            disabled={experiments.length === 0}
            className="px-2.5 py-1.5 bg-muted text-foreground rounded text-xs flex items-center space-x-1 hover:bg-muted/80 disabled:opacity-50 transition-colors"
          >
            <Download className="h-3 w-3" />
          </button>
          
          <label className="px-2.5 py-1.5 bg-muted text-foreground rounded text-xs flex items-center space-x-1 cursor-pointer hover:bg-muted/80 transition-colors">
            <Upload className="h-3 w-3" />
            <input
              type="file"
              accept=".json"
              onChange={handleImportExperiments}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card border rounded-lg p-5 w-80 shadow-lg">
            <h3 className="text-sm font-semibold mb-3">Save Experiment</h3>
            <input
              type="text"
              placeholder="Name..."
              value={experimentName}
              onChange={(e) => setExperimentName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input bg-background rounded focus:outline-none focus:ring-1 focus:ring-ring mb-3"
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExperiment}
                className="px-3 py-1.5 bg-foreground text-background rounded text-sm hover:bg-foreground/90 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Experiments List */}
      <div className="space-y-2">
        {experiments.length === 0 ? (
          <div className="text-center text-muted-foreground py-6">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No saved experiments</p>
          </div>
        ) : (
          experiments.map((experiment) => (
            <div
              key={experiment.id}
              className={cn(
                "border rounded-md p-3 transition-colors",
                selectedExperiments.includes(experiment.id) && "border-foreground/30 bg-muted/30"
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedExperiments.includes(experiment.id)}
                      onChange={() => toggleExperimentSelection(experiment.id)}
                      className="rounded border-input"
                    />
                    <h3 className="font-medium text-sm truncate">{experiment.name}</h3>
                  </div>
                  
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground mb-2">
                    <div className="flex items-center space-x-0.5">
                      <Calendar className="h-2.5 w-2.5" />
                      <span>{formatDate(experiment.timestamp)}</span>
                    </div>
                    <div className="flex items-center space-x-0.5">
                      <Users className="h-2.5 w-2.5" />
                      <span>{formatNumber(experiment.parameters.sample_size)}</span>
                    </div>
                    <div className="flex items-center space-x-0.5">
                      <Target className="h-2.5 w-2.5" />
                      <span>{experiment.parameters.selected_variables.length} vars</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 text-xs">
                    <span className="font-medium">
                      {experiment.results.accuracy ? formatPercentage(experiment.results.accuracy) : 'N/A'}
                    </span>
                    <span className="text-muted-foreground">
                      Yes: {formatNumber(experiment.results.si_count)}
                    </span>
                    <span className="text-muted-foreground">
                      No: {formatNumber(experiment.results.no_count)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => onLoadExperiment(experiment)}
                    className="px-2 py-1 bg-muted text-foreground rounded text-xs hover:bg-muted/80 transition-colors"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => handleDeleteExperiment(experiment.id)}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comparison View */}
      {selectedExperiments.length > 1 && (
        <div className="mt-4 p-3 bg-muted/30 rounded-md">
          <h3 className="text-xs font-medium mb-2">
            Comparison ({selectedExperiments.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1.5 font-medium">Name</th>
                  <th className="py-1.5 font-medium">Accuracy</th>
                  <th className="py-1.5 font-medium">Yes %</th>
                  <th className="py-1.5 font-medium">N</th>
                </tr>
              </thead>
              <tbody>
                {getComparisonData().map((data, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="py-1.5">{data.name}</td>
                    <td className="py-1.5">{(data.accuracy * 100).toFixed(1)}%</td>
                    <td className="py-1.5">{data.si_percentage.toFixed(1)}%</td>
                    <td className="py-1.5">{data.sample_size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperimentManager;
